class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.onDrawerTouchStart = this.onDrawerTouchStart.bind(this);
    this.onDrawerTouchMove = this.onDrawerTouchMove.bind(this);
    this.onDrawerTouchEnd = this.onDrawerTouchEnd.bind(this);
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  disconnectedCallback() {
    this.removeDrawerTouchListeners();
  }

  addDrawerTouchListeners() {
    if (this.drawerTouchListenersActive) return;

    window.addEventListener('touchstart', this.onDrawerTouchStart, { capture: true, passive: true });
    window.addEventListener('touchmove', this.onDrawerTouchMove, { capture: true, passive: false });
    window.addEventListener('touchend', this.onDrawerTouchEnd, { capture: true, passive: true });
    window.addEventListener('touchcancel', this.onDrawerTouchEnd, { capture: true, passive: true });
    this.drawerTouchListenersActive = true;
  }

  removeDrawerTouchListeners() {
    if (!this.drawerTouchListenersActive) return;

    window.removeEventListener('touchstart', this.onDrawerTouchStart, { capture: true });
    window.removeEventListener('touchmove', this.onDrawerTouchMove, { capture: true });
    window.removeEventListener('touchend', this.onDrawerTouchEnd, { capture: true });
    window.removeEventListener('touchcancel', this.onDrawerTouchEnd, { capture: true });
    this.drawerTouchListenersActive = false;
    this.onDrawerTouchEnd();
  }

  onDrawerTouchStart(event) {
    if (!this.classList.contains('active') || event.touches.length !== 1) return;
    if (!(event.target instanceof Element) || !event.target.closest('cart-drawer-items')) return;

    const scroller = this.querySelector('cart-drawer-items');
    if (!scroller || scroller.scrollHeight <= scroller.clientHeight) return;

    this.touchScroller = scroller;
    this.touchStartX = event.touches[0].clientX;
    this.touchStartY = event.touches[0].clientY;
    this.touchLastY = this.touchStartY;
    this.touchDirection = 'pending';
  }

  onDrawerTouchMove(event) {
    if (!this.touchScroller || event.touches.length !== 1) return;

    const currentX = event.touches[0].clientX;
    const currentY = event.touches[0].clientY;
    const totalX = Math.abs(currentX - this.touchStartX);
    const totalY = Math.abs(currentY - this.touchStartY);

    if (this.touchDirection === 'pending' && Math.max(totalX, totalY) > 8) {
      this.touchDirection = totalX > totalY * 1.15 ? 'horizontal' : 'vertical';
    }

    if (this.touchDirection === 'horizontal') {
      this.touchScroller = null;
      return;
    }
    if (this.touchDirection !== 'vertical') return;

    const delta = this.touchLastY - currentY;
    const maxScroll = this.touchScroller.scrollHeight - this.touchScroller.clientHeight;
    const nextScroll = Math.min(maxScroll, Math.max(0, this.touchScroller.scrollTop + delta));

    this.touchLastY = currentY;
    if (nextScroll === this.touchScroller.scrollTop) return;

    this.touchScroller.scrollTop = nextScroll;
    event.preventDefault();
  }

  onDrawerTouchEnd() {
    this.touchScroller = null;
    this.touchStartX = null;
    this.touchStartY = null;
    this.touchLastY = null;
    this.touchDirection = null;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.addDrawerTouchListeners();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    this.lockBodyScroll();
  }

  close() {
    this.classList.remove('active');
    this.removeDrawerTouchListeners();
    removeTrapFocus(this.activeElement);
    this.unlockBodyScroll();
  }

  lockBodyScroll() {
    if (document.documentElement.classList.contains('cart-drawer-open')) return;

    this.scrollY = window.scrollY || document.documentElement.scrollTop || 0;
    document.body.classList.add('overflow-hidden');
    document.documentElement.classList.add('cart-drawer-open');
  }

  unlockBodyScroll() {
    document.body.classList.remove('overflow-hidden');
    document.documentElement.classList.remove('cart-drawer-open');
    this.scrollY = 0;
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
